// StrategyDashboard.js - Phase 2: Marketing Strategy (Tab-based, matching Phase 1 design)
import React, { useState, useEffect, useRef } from 'react';
import { businessAPI } from '../../../services/businessAPI';

const LOADING_STEPS = [
  { text: 'Analysing your Brand DNA...', icon: 'fas fa-dna', duration: 3000 },
  { text: 'Studying your competitors...', icon: 'fas fa-search', duration: 4000 },
  { text: 'Crafting content pillars...', icon: 'fas fa-columns', duration: 5000 },
  { text: 'Building channel strategy...', icon: 'fas fa-project-diagram', duration: 4000 },
  { text: 'Generating 30-day calendar...', icon: 'fas fa-calendar-alt', duration: 6000 },
  { text: 'Finalising your strategy...', icon: 'fas fa-magic', duration: 3000 }
];

const FORMAT_LABELS = {
  reel: 'Reel', carousel: 'Carousel', story: 'Story', static_post: 'Post',
  video: 'Video', short_video: 'Short', text_post: 'Text', article: 'Article',
  thread: 'Thread', newsletter: 'Newsletter', update: 'Update', offer: 'Offer', live: 'Live'
};

const PLATFORM_ICONS = {
  instagram: 'fab fa-instagram', facebook: 'fab fa-facebook', linkedin: 'fab fa-linkedin',
  tiktok: 'fab fa-tiktok', twitter: 'fab fa-twitter', youtube: 'fab fa-youtube',
  email: 'fas fa-envelope', googleBusiness: 'fas fa-map-marker-alt'
};

const CONTENT_TYPE_COLORS = {
  educational: 'bg-blue-100 text-blue-700',
  entertainment: 'bg-pink-100 text-pink-700',
  inspirational: 'bg-amber-100 text-amber-700',
  promotional: 'bg-green-100 text-green-700'
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function StrategyDashboard({ businessData, strategyData, onStrategyUpdate, onPhaseComplete }) {
  const [activeTab, setActiveTab] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [error, setError] = useState(null);
  const [megaStrategy, setMegaStrategy] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isApproved, setIsApproved] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [calendarExpiry, setCalendarExpiry] = useState(null);
  const hasLoadedRef = useRef(false);

  // Tab 1 form state
  const [auditData, setAuditData] = useState({
    primaryGoal: '',
    contentStyle: '',
    instagramFollowers: '',
    facebookFollowers: '',
    linkedinFollowers: '',
    tiktokFollowers: '',
    googleReviewCount: '',
    googleRatingAverage: '',
    optionalModules: { blog: false, email: false }
  });

  const selectedPlatforms = businessData?.preferredPlatforms || [];

  // Load existing strategy data
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadExistingData();
    }
  }, []);

  const loadExistingData = async () => {
    try {
      const response = await businessAPI.getStrategy();
      if (response.success && response.strategy) {
        const s = response.strategy;
        if (s.megaStrategy?.calendar?.length > 0) {
          setMegaStrategy(s.megaStrategy);
          // Check expiry
          checkCalendarExpiry(s.megaStrategy);
          if (s.audit) {
            setAuditData(prev => ({
              ...prev,
              primaryGoal: s.audit.primaryGoal || prev.primaryGoal,
              contentStyle: s.audit.contentStyle || prev.contentStyle,
              instagramFollowers: s.audit.instagramFollowers || '',
              facebookFollowers: s.audit.facebookFollowers || '',
              linkedinFollowers: s.audit.linkedinFollowers || '',
              tiktokFollowers: s.audit.tiktokFollowers || '',
              googleReviewCount: s.audit.googleReviewCount || '',
              googleRatingAverage: s.audit.googleRatingAverage || '',
              optionalModules: s.optionalModules || { blog: false, email: false }
            }));
          }
          if (s.phase2Complete) setIsApproved(true);
          setActiveTab(3); // Go to calendar tab
        } else if (s.audit?.primaryGoal) {
          setAuditData(prev => ({
            ...prev,
            primaryGoal: s.audit.primaryGoal || '',
            contentStyle: s.audit.contentStyle || '',
            instagramFollowers: s.audit.instagramFollowers || '',
            facebookFollowers: s.audit.facebookFollowers || '',
            linkedinFollowers: s.audit.linkedinFollowers || '',
            tiktokFollowers: s.audit.tiktokFollowers || '',
            googleReviewCount: s.audit.googleReviewCount || '',
            googleRatingAverage: s.audit.googleRatingAverage || '',
            optionalModules: s.optionalModules || { blog: false, email: false }
          }));
        }
      }
    } catch (err) {
      console.log('No existing strategy found');
    }
  };

  // Check if calendar has expired (30 days from first date)
  const checkCalendarExpiry = (strategy) => {
    if (!strategy?.calendar?.length) return;
    const lastDay = strategy.calendar[strategy.calendar.length - 1];
    if (lastDay?.date) {
      const expiryDate = new Date(lastDay.date);
      expiryDate.setDate(expiryDate.getDate() + 1); // Day after last calendar day
      setCalendarExpiry(expiryDate);
    }
  };

  const isCalendarExpired = () => {
    if (!calendarExpiry) return false;
    return new Date() >= calendarExpiry;
  };

  const getDaysRemaining = () => {
    if (!calendarExpiry) return null;
    const diff = calendarExpiry - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const handleAuditChange = (field, value) => {
    setAuditData(prev => ({ ...prev, [field]: value }));
  };

  const toggleModule = (key) => {
    setAuditData(prev => ({
      ...prev,
      optionalModules: { ...prev.optionalModules, [key]: !prev.optionalModules[key] }
    }));
  };

  const isTab1Valid = () => auditData.primaryGoal && auditData.contentStyle;

  // Generate strategy
  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setLoadingStepIndex(0);
    setActiveTab(2);

    let stepIdx = 0;
    const interval = setInterval(() => {
      stepIdx++;
      if (stepIdx < LOADING_STEPS.length) {
        setLoadingStepIndex(stepIdx);
      }
    }, 4000);

    try {
      // Pass ALL Brand DNA data to the AI
      const brandDna = businessData?.brandDna || {};
      const result = await businessAPI.generateMegaStrategy({
        businessData: {
          businessName: businessData.businessName,
          businessType: businessData.businessType,
          industry: businessData.industry,
          location: businessData.location,
          website: businessData.website,
          directCompetitors: businessData.directCompetitors,
          // Brand DNA enrichment
          primaryService: brandDna.primaryService || '',
          idealCustomer: brandDna.idealCustomer || {},
          brandColours: {
            primary: brandDna.primaryColour || '',
            secondary: brandDna.secondaryColour || '',
            accent: brandDna.accentColour || ''
          },
          fontStyle: brandDna.fontStyle || '',
          contentPreference: brandDna.contentPreference || '',
          reelsPriority: brandDna.reelsPriority || '',
          postingFrequency: brandDna.postingFrequency || '',
          captionTone: brandDna.captionTone || [],
          useEmojis: brandDna.useEmojis || '',
          contentRestrictions: brandDna.contentRestrictions || '',
          existingMediaUrl: brandDna.existingMediaUrl || '',
          styleExamples: (brandDna.styleExamples || []).filter(Boolean)
        },
        auditData: {
          ...auditData,
          primaryGoal: auditData.primaryGoal,
          contentStyle: auditData.contentStyle
        },
        preferences: {
          selectedPlatforms,
          primaryGoal: auditData.primaryGoal,
          contentStyle: auditData.contentStyle,
          optionalModules: auditData.optionalModules
        }
      });

      clearInterval(interval);

      if (result.success && result.megaStrategy) {
        setMegaStrategy(result.megaStrategy);
        checkCalendarExpiry(result.megaStrategy);
        setIsApproved(false);
        setActiveTab(3);
      } else {
        setError('Failed to generate strategy. Please try again.');
      }
    } catch (err) {
      clearInterval(interval);
      console.error('Mega strategy error:', err);
      setError(err.details || err.error || err.message || 'Generation failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Edit day handlers
  const startEditDay = (dayIndex) => {
    const day = megaStrategy.calendar[dayIndex];
    setEditingDay(dayIndex);
    setEditForm({
      postTitle: day.postTitle || '',
      caption: day.caption || '',
      platform: day.platform || 'instagram',
      contentType: day.contentType || 'educational',
      format: day.format || 'post',
      hashtags: (day.hashtags || []).join(', ')
    });
  };

  const saveEditDay = async () => {
    if (editingDay === null) return;
    const updated = { ...megaStrategy };
    updated.calendar = [...updated.calendar];
    updated.calendar[editingDay] = {
      ...updated.calendar[editingDay],
      postTitle: editForm.postTitle,
      caption: editForm.caption,
      platform: editForm.platform,
      contentType: editForm.contentType,
      format: editForm.format,
      hashtags: editForm.hashtags.split(',').map(h => h.trim()).filter(Boolean)
    };
    setMegaStrategy(updated);
    setEditingDay(null);
    setEditForm({});

    // Persist changes to DB so edits survive page refresh
    try {
      await businessAPI.saveStrategy({ megaStrategy: updated });
      // Also sync to roadmap calendar so changes appear in Phase 3
      const dayNumber = editingDay + 1;
      await businessAPI.updateDayContent(dayNumber, {
        postTitle: updated.calendar[editingDay].postTitle,
        caption: updated.calendar[editingDay].caption,
        platform: updated.calendar[editingDay].platform,
        contentType: updated.calendar[editingDay].contentType,
        format: updated.calendar[editingDay].format,
        hashtags: updated.calendar[editingDay].hashtags
      });
    } catch (err) {
      console.error('Failed to save strategy day edit:', err);
    }
  };

  const cancelEdit = () => {
    setEditingDay(null);
    setEditForm({});
  };

  // Approve
  const handleApprove = async () => {
    try {
      await businessAPI.approveMegaStrategy();
      setIsApproved(true);
      if (onStrategyUpdate) {
        onStrategyUpdate({
          ...strategyData,
          megaStrategy,
          selectedPlatforms,
          optionalModules: auditData.optionalModules,
          strategyApproved: true,
          proposalApproved: true,
          phase2Complete: true
        });
      }
    } catch (err) {
      console.error('Approve error:', err);
      setError('Failed to approve. Please try again.');
    }
  };

  const handleLaunchCalendar = async () => {
    // Delete the existing roadmap from DB so the new strategy calendar is saved fresh.
    // Without this, RoadmapDashboard finds the old calendar and ignores the new megaStrategy.
    try {
      await businessAPI.deleteRoadmap();
    } catch (err) {
      // Ignore — roadmap may not exist yet (first-time users)
    }
    if (onPhaseComplete) onPhaseComplete();
  };

  // Renew calendar (generate next 30 days)
  const handleRenewCalendar = () => {
    setIsApproved(false);
    setMegaStrategy(null);
    handleGenerate();
  };

  // Download Report
  const handleDownloadReport = () => {
    if (!megaStrategy) return;
    setIsDownloading(true);

    try {
      const s = megaStrategy.strategySummary || {};
      const ch = megaStrategy.channelStrategy || {};
      const cal = megaStrategy.calendar || [];
      const biz = businessData || {};
      const brandDna = biz.brandDna || {};

      // Build tentative outcomes HTML separately (avoids nested template literal issues)
      let outcomesHtml = '';
      if (megaStrategy.tentativeOutcomes) {
        const o = megaStrategy.tentativeOutcomes;
        const toArr = (v) => Array.isArray(v) ? v : (typeof v === 'string' && v ? [v] : []);
        const metrics = toArr(o.metrics);
        const metricsRows = metrics.map(m => {
          if (typeof m === 'string') return '<tr><td colspan="5">' + m + '</td></tr>';
          return '<tr><td style="font-weight:600">' + (m.metric || '') + '</td><td>' + (m.current || 'N/A') + '</td><td>' + (m.projected || 'N/A') + '</td><td style="color:#16a34a;font-weight:600">' + (m.change || '-') + '</td><td style="font-size:10px;color:#64748b">' + (m.justification || '') + '</td></tr>';
        }).join('');
        const winsItems = toArr(o.keyWins).map(w => '<li style="margin-bottom:3px">' + w + '</li>').join('');
        const risksItems = toArr(o.risks).map(r => '<li style="margin-bottom:3px">' + r + '</li>').join('');
        const nextItems = toArr(o.nextSteps).map(n => '<li style="margin-bottom:3px">' + n + '</li>').join('');

        outcomesHtml = '<div class="section">' +
          '<div class="section-title">Projected Outcomes (AI-Generated Estimates)</div>' +
          '<p style="font-size:11px;color:#64748b;margin-bottom:12px;font-style:italic">The following projections are AI-generated estimates based on your business profile and industry benchmarks. Actual results may vary based on execution quality, market conditions, and audience engagement.</p>' +
          (o.summary ? '<div class="card" style="margin-bottom:12px"><p style="font-size:12px">' + o.summary + '</p></div>' : '') +
          (metricsRows ? '<table><tr><th>Metric</th><th>Current</th><th>Projected</th><th>Change</th><th>Justification</th></tr>' + metricsRows + '</table>' : '') +
          '<div class="grid-2" style="margin-top:12px">' +
          (winsItems ? '<div class="card"><label>Key Wins to Expect</label><ul style="margin-top:6px;padding-left:16px;font-size:11px;color:#334155">' + winsItems + '</ul></div>' : '') +
          (risksItems ? '<div class="card"><label>Risks & Considerations</label><ul style="margin-top:6px;padding-left:16px;font-size:11px;color:#334155">' + risksItems + '</ul></div>' : '') +
          '</div>' +
          (nextItems ? '<div class="card" style="margin-top:10px"><label>Recommended Next Steps (Post 30 Days)</label><ul style="margin-top:6px;padding-left:16px;font-size:11px;color:#334155">' + nextItems + '</ul></div>' : '') +
          '</div>';
      }

      const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>30-Day Marketing Strategy - ${biz.businessName || 'Your Business'}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', -apple-system, sans-serif; color: #1e293b; background: #fff; line-height: 1.5; }
  .page { max-width: 900px; margin: 0 auto; padding: 40px; }
  .header { background: #1e40af; color: #fff; padding: 32px 40px; margin: -40px -40px 32px; }
  .header h1 { font-size: 24px; font-weight: 700; margin-bottom: 4px; }
  .header p { font-size: 13px; opacity: 0.8; }
  .header .meta { display: flex; gap: 20px; margin-top: 12px; font-size: 11px; opacity: 0.7; }
  .section { margin-bottom: 24px; }
  .section-title { font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #e2e8f0; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
  .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; }
  .card label { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; }
  .card p { font-size: 12px; color: #334155; margin-top: 3px; }
  .pill { display: inline-block; background: #dbeafe; color: #1d4ed8; font-size: 10px; padding: 2px 8px; border-radius: 10px; margin: 2px; font-weight: 500; }
  .channel-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; text-align: center; }
  .channel-card .name { font-size: 13px; font-weight: 700; text-transform: capitalize; }
  .channel-card .pct { font-size: 20px; font-weight: 800; color: #2563eb; }
  .channel-card .freq { font-size: 10px; color: #64748b; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
  table th { background: #f1f5f9; color: #475569; padding: 6px 8px; text-align: left; font-weight: 600; font-size: 10px; border-bottom: 2px solid #e2e8f0; }
  table td { padding: 6px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  table tr:nth-child(even) td { background: #fafbfc; }
  .badge { display: inline-block; font-size: 9px; padding: 1px 6px; border-radius: 8px; font-weight: 500; }
  .badge-edu { background: #dbeafe; color: #1d4ed8; }
  .badge-ent { background: #fce7f3; color: #be185d; }
  .badge-ins { background: #fef3c7; color: #b45309; }
  .badge-pro { background: #dcfce7; color: #15803d; }
  .brand-colours { display: flex; gap: 4px; margin-top: 4px; }
  .colour-swatch { width: 24px; height: 24px; border-radius: 4px; border: 1px solid #e2e8f0; }
  .footer { text-align: center; font-size: 10px; color: #94a3b8; margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
  @media print { .page { padding: 20px; } .header { margin: -20px -20px 20px; padding: 20px; } }
</style>
</head>
<body>
<div class="page">
  <div class="header">
    <h1>30-Day Marketing Strategy</h1>
    <p>${biz.businessName || ''} - ${biz.industry || ''}</p>
    <div class="meta">
      <span>Location: ${biz.location || 'N/A'}</span>
      <span>Generated: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
      <span>${cal.length} Days</span>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Strategy Summary</div>
    <div class="grid-2">
      <div class="card"><label>Target Audience</label><p>${s.targetAudience || 'N/A'}</p></div>
      <div class="card"><label>Unique Value</label><p>${s.uniqueValue || 'N/A'}</p></div>
      <div class="card"><label>Positioning</label><p>${s.positioning || 'N/A'}</p></div>
      <div class="card"><label>30-Day Goal</label><p>${s.goal || 'N/A'}</p></div>
    </div>
    ${s.contentPillars ? `<div style="margin-top:10px"><label style="font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#64748b">Content Pillars</label><div style="margin-top:4px">${s.contentPillars.map(p => `<span class="pill">${p}</span>`).join('')}</div></div>` : ''}
  </div>

  <div class="section">
    <div class="section-title">Channel Strategy</div>
    <div class="grid-3">
      ${['primary', 'secondary', 'tertiary'].map(tier => {
        const c = ch[tier];
        if (!c) return '';
        return `<div class="channel-card">
          <div class="name">${c.name}</div>
          <div class="pct">${c.percentage}%</div>
          <div class="freq">${c.frequency}</div>
          <div style="margin-top:4px">${(c.formats || []).map(f => `<span class="pill">${f}</span>`).join('')}</div>
        </div>`;
      }).join('')}
    </div>
  </div>

  ${brandDna.primaryColour ? `<div class="section">
    <div class="section-title">Brand Identity</div>
    <div class="grid-2">
      <div class="card">
        <label>Brand Colours</label>
        <div class="brand-colours">
          <div class="colour-swatch" style="background:${brandDna.primaryColour}"></div>
          <div class="colour-swatch" style="background:${brandDna.secondaryColour || '#666'}"></div>
          <div class="colour-swatch" style="background:${brandDna.accentColour || '#3B82F6'}"></div>
        </div>
      </div>
      <div class="card"><label>Caption Tone</label><p>${Array.isArray(brandDna.captionTone) ? brandDna.captionTone.join(', ') : brandDna.captionTone || 'N/A'}</p></div>
    </div>
  </div>` : ''}

  <div class="section">
    <div class="section-title">30-Day Content Calendar</div>
    <table>
      <tr><th>Day</th><th>Date</th><th>Platform</th><th>Post Title</th><th>Type</th><th>Format</th></tr>
      ${cal.map(d => {
        const dateStr = new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        const badgeClass = d.contentType === 'educational' ? 'badge-edu' : d.contentType === 'entertainment' ? 'badge-ent' : d.contentType === 'inspirational' ? 'badge-ins' : 'badge-pro';
        return `<tr><td>${d.day}</td><td>${dateStr}</td><td style="text-transform:capitalize">${d.platform}</td><td>${d.postTitle}</td><td><span class="badge ${badgeClass}">${d.contentType}</span></td><td>${FORMAT_LABELS[d.format] || d.format || '-'}</td></tr>`;
      }).join('')}
    </table>
  </div>

  ${outcomesHtml}

  <div class="footer">
    Generated by Digital Trent Marketing Studio | ${new Date().toLocaleDateString('en-GB')}<br>
    <span style="font-size:9px">All projections and strategy recommendations are AI-generated estimates and should be used as guidance only.</span>
  </div>
</div>
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(biz.businessName || 'Strategy').replace(/[^a-zA-Z0-9]/g, '_')}_30Day_Strategy.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download report.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Shared styles
  const inputClass = 'w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white placeholder-slate-400';
  const labelClass = 'block text-xs font-semibold text-slate-600 mb-1';

  const tabComplete = (num) => {
    if (num === 1) return auditData.primaryGoal && auditData.contentStyle;
    if (num === 2) return megaStrategy?.strategySummary;
    if (num === 3) return megaStrategy?.calendar?.length > 0;
    if (num === 4) return megaStrategy?.strategySummary;
    if (num === 5) return isApproved;
    return false;
  };

  const TABS = [
    { num: 1, title: 'Goals & Presence', icon: 'fas fa-bullseye' },
    { num: 2, title: 'Generate', icon: 'fas fa-wand-magic-sparkles' },
    { num: 3, title: 'Content Calendar', icon: 'fas fa-calendar-alt' },
    { num: 4, title: 'Report', icon: 'fas fa-file-alt' },
    { num: 5, title: 'Approve & Launch', icon: 'fas fa-rocket' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <i className="fas fa-chess-queen text-white text-sm"></i>
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Marketing Strategy</h1>
            <p className="text-xs text-slate-500">Generate your AI-powered 30-day content strategy</p>
          </div>
          {calendarExpiry && megaStrategy && (
            <div className={`ml-auto px-3 py-1.5 rounded-lg text-[11px] font-semibold ${
              isCalendarExpired() ? 'bg-red-50 text-red-700 border border-red-200' :
              getDaysRemaining() <= 7 ? 'bg-amber-50 text-amber-700 border border-amber-200' :
              'bg-green-50 text-green-700 border border-green-200'
            }`}>
              <i className={`fas ${isCalendarExpired() ? 'fa-exclamation-circle' : 'fa-clock'} mr-1`}></i>
              {isCalendarExpired() ? 'Calendar expired - renew now' : `${getDaysRemaining()} days remaining`}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><i className="fas fa-times"></i></button>
        </div>
      )}

      {/* Tab Navigation (matching Phase 1 style) */}
      <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-lg p-1">
        {TABS.map((t) => (
          <button key={t.num} onClick={() => {
            // Only allow navigation to tabs that make sense
            if (t.num <= 2 || megaStrategy) setActiveTab(t.num);
          }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[11px] font-semibold transition-all ${
              activeTab === t.num
                ? 'bg-white text-slate-800 shadow-sm'
                : (t.num > 2 && !megaStrategy)
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tabComplete(t.num) ? (
              <i className="fas fa-check-circle text-green-500 text-[10px]"></i>
            ) : (
              <i className={`${t.icon} text-[10px]`}></i>
            )}
            <span className="hidden sm:inline">{t.title}</span>
            <span className="sm:hidden">{t.num}</span>
          </button>
        ))}
      </div>

      {/* ============ TAB 1: Goals & Online Presence ============ */}
      {activeTab === 1 && (
        <div className="space-y-4">
          {/* Marketing Goals */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">1</div>
              <h2 className="text-sm font-bold text-slate-800">Marketing Goals</h2>
              <span className="text-[10px] text-slate-400 ml-auto">Tell us what you want to achieve</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Primary Goal <span className="text-red-500">*</span></label>
                <select value={auditData.primaryGoal} onChange={(e) => handleAuditChange('primaryGoal', e.target.value)} className={inputClass}>
                  <option value="">Select your main goal...</option>
                  <option value="brand_awareness">Brand Awareness</option>
                  <option value="lead_generation">Lead Generation</option>
                  <option value="sales_growth">Sales Growth</option>
                  <option value="community_building">Community Building</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Content Style <span className="text-red-500">*</span></label>
                <select value={auditData.contentStyle} onChange={(e) => handleAuditChange('contentStyle', e.target.value)} className={inputClass}>
                  <option value="">Select content style...</option>
                  <option value="professional">Professional</option>
                  <option value="casual">Casual & Friendly</option>
                  <option value="mixed">Mixed</option>
                  <option value="bold">Bold & Creative</option>
                </select>
              </div>
            </div>
          </div>

          {/* Online Presence */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-xs font-bold text-slate-800 mb-1 uppercase tracking-wider">
              <i className="fas fa-globe text-blue-600 mr-1.5"></i>Current Online Presence
            </h3>
            <p className="text-[11px] text-slate-400 mb-3">Optional - helps AI personalise your strategy</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {selectedPlatforms.includes('instagram') && (
                <div>
                  <label className={labelClass}><i className="fab fa-instagram mr-1"></i>Instagram Followers</label>
                  <input type="number" value={auditData.instagramFollowers} onChange={(e) => handleAuditChange('instagramFollowers', e.target.value)}
                    placeholder="0" className={inputClass} />
                </div>
              )}
              {selectedPlatforms.includes('facebook') && (
                <div>
                  <label className={labelClass}><i className="fab fa-facebook mr-1"></i>Facebook Followers</label>
                  <input type="number" value={auditData.facebookFollowers} onChange={(e) => handleAuditChange('facebookFollowers', e.target.value)}
                    placeholder="0" className={inputClass} />
                </div>
              )}
              {selectedPlatforms.includes('linkedin') && (
                <div>
                  <label className={labelClass}><i className="fab fa-linkedin mr-1"></i>LinkedIn Followers</label>
                  <input type="number" value={auditData.linkedinFollowers} onChange={(e) => handleAuditChange('linkedinFollowers', e.target.value)}
                    placeholder="0" className={inputClass} />
                </div>
              )}
              {selectedPlatforms.includes('tiktok') && (
                <div>
                  <label className={labelClass}><i className="fab fa-tiktok mr-1"></i>TikTok Followers</label>
                  <input type="number" value={auditData.tiktokFollowers} onChange={(e) => handleAuditChange('tiktokFollowers', e.target.value)}
                    placeholder="0" className={inputClass} />
                </div>
              )}
              <div>
                <label className={labelClass}><i className="fas fa-star mr-1"></i>Google Reviews</label>
                <input type="number" value={auditData.googleReviewCount} onChange={(e) => handleAuditChange('googleReviewCount', e.target.value)}
                  placeholder="0" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}><i className="fas fa-star-half-alt mr-1"></i>Avg Rating</label>
                <input type="number" step="0.1" min="1" max="5" value={auditData.googleRatingAverage} onChange={(e) => handleAuditChange('googleRatingAverage', e.target.value)}
                  placeholder="4.5" className={inputClass} />
              </div>
            </div>
          </div>

          {/* Optional Modules */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-xs font-bold text-slate-800 mb-3 uppercase tracking-wider">
              <i className="fas fa-puzzle-piece text-blue-600 mr-1.5"></i>Optional Content Modules
            </h3>
            <div className="flex gap-3">
              {[
                { key: 'blog', label: 'Blog Campaign', icon: 'fas fa-blog', desc: 'AI-generated blog posts' },
                { key: 'email', label: 'Email Campaign', icon: 'fas fa-envelope-open-text', desc: 'Newsletters & sequences' }
              ].map(mod => (
                <button key={mod.key} onClick={() => toggleModule(mod.key)}
                  className={`flex-1 p-3 rounded-lg border text-left transition-all ${
                    auditData.optionalModules[mod.key]
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <i className={`${mod.icon} text-sm ${auditData.optionalModules[mod.key] ? 'text-blue-600' : 'text-slate-400'}`}></i>
                    <span className={`text-xs font-semibold ${auditData.optionalModules[mod.key] ? 'text-blue-700' : 'text-slate-700'}`}>{mod.label}</span>
                  </div>
                  <p className="text-[11px] text-slate-400">{mod.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Brand DNA Preview */}
          {businessData?.brandDna && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-wider">
                <i className="fas fa-dna text-blue-500 mr-1"></i>Brand DNA from Phase 1 (will be used by AI)
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-[10px]">
                {businessData.brandDna.primaryService && (
                  <div><span className="text-slate-400">Service:</span> <span className="text-slate-700 font-medium">{businessData.brandDna.primaryService}</span></div>
                )}
                {businessData.brandDna.fontStyle && (
                  <div><span className="text-slate-400">Font:</span> <span className="text-slate-700 font-medium">{businessData.brandDna.fontStyle.replace(/_/g, ' ')}</span></div>
                )}
                {businessData.brandDna.contentPreference && (
                  <div><span className="text-slate-400">Content:</span> <span className="text-slate-700 font-medium">{businessData.brandDna.contentPreference.replace(/_/g, ' ')}</span></div>
                )}
                {businessData.brandDna.postingFrequency && (
                  <div><span className="text-slate-400">Frequency:</span> <span className="text-slate-700 font-medium">{{weekdays:'5 days/week (Mon–Fri)', daily:'7 days/week'}[businessData.brandDna.postingFrequency] || businessData.brandDna.postingFrequency.replace(/_/g, ' ')}</span></div>
                )}
                {businessData.brandDna.captionTone?.length > 0 && (
                  <div><span className="text-slate-400">Tone:</span> <span className="text-slate-700 font-medium">{businessData.brandDna.captionTone.join(', ')}</span></div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={() => { handleGenerate(); }} disabled={!isTab1Valid()}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${
                isTab1Valid() ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}>
              <i className="fas fa-wand-magic-sparkles"></i> Generate My 30-Day Strategy
            </button>
          </div>
        </div>
      )}

      {/* ============ TAB 2: Generating ============ */}
      {activeTab === 2 && (
        <div>
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center mb-5">
                <i className={`${LOADING_STEPS[loadingStepIndex]?.icon || 'fas fa-spinner fa-spin'} text-white text-xl`}></i>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Building Your Strategy</h2>
              <p className="text-sm text-blue-600 font-medium mb-6">{LOADING_STEPS[loadingStepIndex]?.text || 'Almost there...'}</p>
              <div className="w-full max-w-md">
                <div className="space-y-2">
                  {LOADING_STEPS.map((s, i) => (
                    <div key={i} className={`flex items-center gap-3 text-xs transition-all duration-500 ${
                      i < loadingStepIndex ? 'text-green-600' : i === loadingStepIndex ? 'text-blue-600 font-medium' : 'text-slate-300'
                    }`}>
                      <i className={`${i < loadingStepIndex ? 'fas fa-check-circle' : i === loadingStepIndex ? 'fas fa-spinner fa-spin' : 'far fa-circle'} w-4`}></i>
                      {s.text}
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => { setActiveTab(1); setIsGenerating(false); }}
                className="mt-8 px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                <i className="fas fa-arrow-left mr-1.5"></i>Back to Goals
              </button>
            </div>
          ) : !megaStrategy ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                <i className="fas fa-exclamation-triangle text-red-500 text-xl"></i>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Generation Failed</h2>
              <p className="text-sm text-slate-500 mb-6">{error || 'Something went wrong. Please try again.'}</p>
              <div className="flex gap-3">
                <button onClick={() => setActiveTab(1)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                  <i className="fas fa-arrow-left mr-1.5"></i>Back
                </button>
                <button onClick={handleGenerate}
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                  <i className="fas fa-redo mr-1.5"></i>Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                <i className="fas fa-check-circle text-green-600 text-xl"></i>
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Strategy Generated Successfully</h2>
              <p className="text-sm text-slate-500 mb-6">Review your {megaStrategy.calendar?.length || 30}-day content calendar</p>
              <button onClick={() => setActiveTab(3)}
                className="px-5 py-2.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
                View Content Calendar <i className="fas fa-arrow-right ml-1.5"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============ TAB 3: Content Calendar (30 days flat) ============ */}
      {activeTab === 3 && megaStrategy && (
        <div className="space-y-4">
          {/* Expired calendar banner */}
          {isCalendarExpired() && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-amber-800"><i className="fas fa-exclamation-triangle mr-1.5"></i>Your 30-day calendar has expired</p>
                <p className="text-[11px] text-amber-600 mt-0.5">Generate a fresh 30-day strategy to keep your content flowing</p>
              </div>
              <button onClick={handleRenewCalendar}
                className="px-4 py-2 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors">
                <i className="fas fa-sync-alt mr-1.5"></i>Renew Calendar
              </button>
            </div>
          )}

          {/* Strategy Summary Card */}
          {megaStrategy.strategySummary && (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="text-xs font-bold text-slate-800 mb-3 uppercase tracking-wider">
                <i className="fas fa-lightbulb text-amber-500 mr-1.5"></i>Strategy Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Target Audience</label>
                  <p className="text-xs text-slate-700 mt-1">{megaStrategy.strategySummary.targetAudience}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Unique Value</label>
                  <p className="text-xs text-slate-700 mt-1">{megaStrategy.strategySummary.uniqueValue}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Positioning</label>
                  <p className="text-xs text-slate-700 italic mt-1">{megaStrategy.strategySummary.positioning}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">30-Day Goal</label>
                  <p className="text-xs text-slate-700 mt-1">{megaStrategy.strategySummary.goal}</p>
                </div>
              </div>
              {megaStrategy.strategySummary.contentPillars && (
                <div className="mt-3">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Content Pillars</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {megaStrategy.strategySummary.contentPillars.map((p, i) => (
                      <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[11px] font-medium rounded-full border border-blue-200">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Channel Strategy */}
          {megaStrategy.channelStrategy && (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="text-xs font-bold text-slate-800 mb-3 uppercase tracking-wider">
                <i className="fas fa-project-diagram text-blue-600 mr-1.5"></i>Channel Strategy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['primary', 'secondary', 'tertiary'].map((tier) => {
                  const ch = megaStrategy.channelStrategy[tier];
                  if (!ch) return null;
                  const tierLabels = { primary: 'Primary', secondary: 'Secondary', tertiary: 'Tertiary' };
                  return (
                    <div key={tier} className="rounded-lg border border-slate-200 overflow-hidden">
                      <div className="bg-slate-100 px-3 py-2 flex items-center justify-between border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          <i className={`${PLATFORM_ICONS[ch.name] || 'fas fa-globe'} text-xs text-slate-600`}></i>
                          <span className="text-xs font-semibold text-slate-700 capitalize">{ch.name}</span>
                        </div>
                        <span className="text-[10px] font-bold text-blue-600">{tierLabels[tier]} - {ch.percentage}%</span>
                      </div>
                      <div className="p-3">
                        <p className="text-[11px] text-slate-500 mb-2">{ch.frequency}</p>
                        <div className="flex flex-wrap gap-1">
                          {ch.formats?.map((f, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-50 rounded border border-slate-200 text-slate-600 font-medium">{f}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calendar - date-based grid (handles weekdays-only mode correctly) */}
          {megaStrategy.calendar && (() => {
            const postingFreq = businessData?.brandDna?.postingFrequency || 'daily';
            const isWeekdaysMode = postingFreq === 'weekdays';

            // Build lookup: date string → { entry, index }
            const entryByDate = {};
            megaStrategy.calendar.forEach((entry, idx) => {
              if (entry.date) entryByDate[entry.date] = { entry, idx };
            });

            // Determine grid date range from actual entry dates
            const sortedDates = megaStrategy.calendar.filter(e => e.date).map(e => e.date).sort();
            if (!sortedDates.length) return null;

            const firstEntryDate = new Date(sortedDates[0] + 'T00:00:00');
            const lastEntryDate = new Date(sortedDates[sortedDates.length - 1] + 'T00:00:00');

            // Extend to full Sun-Sat weeks
            const gridStart = new Date(firstEntryDate);
            gridStart.setDate(gridStart.getDate() - gridStart.getDay()); // back to Sunday
            const gridEnd = new Date(lastEntryDate);
            gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay())); // forward to Saturday

            // Build weeks array
            const weeks = [];
            const cur = new Date(gridStart);
            while (cur <= gridEnd) {
              const week = [];
              for (let d = 0; d < 7; d++) {
                week.push(cur.toISOString().split('T')[0]);
                cur.setDate(cur.getDate() + 1);
              }
              weeks.push(week);
            }

            return (
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    <i className="fas fa-calendar-alt text-green-600 mr-1.5"></i>
                    {isWeekdaysMode ? 'Weekday Content Calendar' : '30-Day Content Calendar'}
                    <span className="text-slate-400 font-normal normal-case ml-2">({megaStrategy.calendar.length} posts)</span>
                  </h3>
                  {isWeekdaysMode && (
                    <span className="text-[10px] text-slate-400 italic flex items-center gap-1">
                      <i className="fas fa-briefcase text-[9px]"></i> Mon–Fri only
                    </span>
                  )}
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-t-lg overflow-hidden border border-slate-200">
                  {DAY_NAMES.map(d => (
                    <div key={d} className={`text-center py-1.5 text-[10px] font-bold uppercase ${(d === 'Sat' || d === 'Sun') && isWeekdaysMode ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>{d}</div>
                  ))}
                </div>

                {/* Calendar weeks — date-based lookup */}
                {weeks.map((week, weekIdx) => (
                  <div key={weekIdx} className="grid grid-cols-7 gap-px bg-slate-200 overflow-hidden border-x border-slate-200">
                    {week.map(dateStr => {
                      const hit = entryByDate[dateStr];
                      const dateObj = new Date(dateStr + 'T00:00:00');
                      const dow = dateObj.getDay(); // 0=Sun, 6=Sat
                      const isWeekend = dow === 0 || dow === 6;
                      const dateLabel = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

                      // No entry for this cell
                      if (!hit) {
                        return (
                          <div key={dateStr} className={`p-2 min-h-[90px] flex items-center justify-center ${isWeekend && isWeekdaysMode ? 'bg-slate-100' : 'bg-slate-50'}`}>
                            {isWeekend && isWeekdaysMode && (
                              <span className="text-[9px] text-slate-300 italic">Rest</span>
                            )}
                          </div>
                        );
                      }

                      const { entry: day, idx: entryIdx } = hit;
                      const isEditing = editingDay === entryIdx;
                      const dayName = DAY_NAMES[dow];

                      if (isEditing) {
                        return (
                          <div key={dateStr} className="bg-blue-50 p-2 col-span-7">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-slate-700">Day {day.day} - {dayName} {dateLabel}</span>
                              <div className="flex gap-1.5">
                                <button onClick={saveEditDay} className="px-2.5 py-1 text-[10px] font-semibold bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                                  <i className="fas fa-check mr-1"></i>Save
                                </button>
                                <button onClick={cancelEdit} className="px-2.5 py-1 text-[10px] font-semibold bg-slate-200 text-slate-600 rounded hover:bg-slate-300 transition-colors">
                                  Cancel
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500">Platform</label>
                                <select value={editForm.platform} onChange={(e) => setEditForm(p => ({...p, platform: e.target.value}))}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                                  {Object.keys(PLATFORM_ICONS).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500">Type</label>
                                <select value={editForm.contentType} onChange={(e) => setEditForm(p => ({...p, contentType: e.target.value}))}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                                  <option value="educational">Educational</option>
                                  <option value="entertainment">Entertainment</option>
                                  <option value="inspirational">Inspirational</option>
                                  <option value="promotional">Promotional</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500">Format</label>
                                <select value={editForm.format} onChange={(e) => setEditForm(p => ({...p, format: e.target.value}))}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                                  {Object.entries(FORMAT_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500">Hashtags</label>
                                <input type="text" value={editForm.hashtags} onChange={(e) => setEditForm(p => ({...p, hashtags: e.target.value}))}
                                  placeholder="#tag1, #tag2" className="w-full px-2 py-1 text-xs border border-slate-300 rounded" />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500">Post Title</label>
                                <input type="text" value={editForm.postTitle} onChange={(e) => setEditForm(p => ({...p, postTitle: e.target.value}))}
                                  className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded" />
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500">Caption</label>
                                <textarea value={editForm.caption} onChange={(e) => setEditForm(p => ({...p, caption: e.target.value}))}
                                  rows={2} className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded" />
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={dateStr} className={`p-2 hover:bg-slate-50 transition-colors group min-h-[90px] flex flex-col ${isWeekend ? 'bg-slate-50' : 'bg-white'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1">
                              <i className={`${PLATFORM_ICONS[day.platform] || 'fas fa-globe'} text-[9px] text-slate-400`}></i>
                              <span className="text-[10px] font-semibold text-slate-500">{dateLabel}</span>
                            </div>
                            <button onClick={() => startEditDay(entryIdx)}
                              className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded bg-slate-100 hover:bg-blue-100 flex items-center justify-center transition-all">
                              <i className="fas fa-pencil text-[8px] text-slate-400 hover:text-blue-600"></i>
                            </button>
                          </div>
                          <p className="text-[10px] font-medium text-slate-700 leading-tight line-clamp-2 flex-1">{day.postTitle}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${CONTENT_TYPE_COLORS[day.contentType] || 'bg-slate-100 text-slate-600'}`}>
                              {day.contentType}
                            </span>
                            {day.format && (
                              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
                                {FORMAT_LABELS[day.format] || day.format}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div className="border-x border-b border-slate-200 rounded-b-lg"></div>
              </div>
            );
          })()}

          <div className="flex justify-between">
            <button onClick={() => setActiveTab(1)}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
              <i className="fas fa-arrow-left mr-1.5"></i>Back
            </button>
            <button onClick={() => setActiveTab(4)}
              className="px-5 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              Next: Report <i className="fas fa-arrow-right ml-1.5"></i>
            </button>
          </div>
        </div>
      )}

      {/* ============ TAB 4: Report ============ */}
      {activeTab === 4 && megaStrategy && (() => {
        const rawOutcomes = megaStrategy.tentativeOutcomes;
        const safeArr = (v) => Array.isArray(v) ? v : [];
        const outcomes = rawOutcomes ? {
          ...rawOutcomes,
          metrics: safeArr(rawOutcomes.metrics),
          keyWins: safeArr(rawOutcomes.keyWins),
          risks: safeArr(rawOutcomes.risks),
          nextSteps: Array.isArray(rawOutcomes.nextSteps) ? rawOutcomes.nextSteps : (typeof rawOutcomes.nextSteps === 'string' ? [rawOutcomes.nextSteps] : [])
        } : null;
        return (
        <div className="space-y-4">
          {/* Strategy Overview Card */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold"><i className="fas fa-file-alt text-[9px]"></i></div>
              <h2 className="text-sm font-bold text-slate-800">Strategy Report</h2>
              <span className="text-[10px] text-slate-400 ml-auto">Review projected outcomes & download report</span>
            </div>

            {/* Report preview */}
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
              <div className="bg-blue-600 px-4 py-3">
                <h3 className="text-sm font-bold text-white">30-Day Marketing Strategy</h3>
                <p className="text-[11px] text-blue-200">{businessData?.businessName} - {businessData?.industry}</p>
              </div>

              <div className="p-4 space-y-3">
                {/* Summary stats */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-blue-600">{megaStrategy.calendar?.length || 30}</p>
                    <p className="text-[10px] text-slate-500 font-medium">Content Days</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-green-600">{megaStrategy.strategySummary?.contentPillars?.length || 4}</p>
                    <p className="text-[10px] text-slate-500 font-medium">Content Pillars</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-purple-600">{selectedPlatforms.length}</p>
                    <p className="text-[10px] text-slate-500 font-medium">Platforms</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-amber-600">
                      {megaStrategy.calendar ? [...new Set(megaStrategy.calendar.map(d => d.format))].length : 0}
                    </p>
                    <p className="text-[10px] text-slate-500 font-medium">Format Types</p>
                  </div>
                </div>

                {/* Content mix breakdown */}
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Content Type Distribution</label>
                  <div className="flex gap-2 mt-1.5">
                    {['educational', 'entertainment', 'inspirational', 'promotional'].map(type => {
                      const count = megaStrategy.calendar?.filter(d => d.contentType === type).length || 0;
                      const pct = megaStrategy.calendar?.length ? Math.round((count / megaStrategy.calendar.length) * 100) : 0;
                      return (
                        <div key={type} className="flex-1 bg-slate-50 rounded-lg p-2 text-center">
                          <p className="text-xs font-bold text-slate-700">{pct}%</p>
                          <p className="text-[9px] text-slate-400 capitalize">{type}</p>
                          <p className="text-[9px] text-slate-300">{count} posts</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Platform distribution */}
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Platform Distribution</label>
                  <div className="flex gap-2 mt-1.5">
                    {[...new Set(megaStrategy.calendar?.map(d => d.platform) || [])].map(platform => {
                      const count = megaStrategy.calendar?.filter(d => d.platform === platform).length || 0;
                      const pct = megaStrategy.calendar?.length ? Math.round((count / megaStrategy.calendar.length) * 100) : 0;
                      return (
                        <div key={platform} className="flex-1 bg-slate-50 rounded-lg p-2 text-center">
                          <i className={`${PLATFORM_ICONS[platform] || 'fas fa-globe'} text-sm text-slate-500 mb-0.5`}></i>
                          <p className="text-xs font-bold text-slate-700">{pct}%</p>
                          <p className="text-[9px] text-slate-400 capitalize">{platform}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Pillars */}
                {megaStrategy.strategySummary?.contentPillars && (
                  <div>
                    <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Content Pillars</label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {megaStrategy.strategySummary.contentPillars.map((p, i) => (
                        <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[11px] font-medium rounded-full border border-blue-200">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tentative Outcomes Card */}
          {outcomes && (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded bg-green-600 text-white flex items-center justify-center text-[10px] font-bold"><i className="fas fa-chart-line text-[9px]"></i></div>
                <h2 className="text-sm font-bold text-slate-800">Tentative Outcomes</h2>
              </div>
              <div className="flex items-center gap-1.5 mb-4">
                <i className="fas fa-robot text-[10px] text-blue-400"></i>
                <p className="text-[10px] text-slate-400 italic">AI-projected estimates based on your business profile, industry benchmarks, and content strategy. Actual results may vary.</p>
              </div>

              {/* Summary */}
              {outcomes.summary && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-800 leading-relaxed">{outcomes.summary}</p>
                </div>
              )}

              {/* Projected Metrics Table */}
              {outcomes.metrics?.length > 0 && (
                <div className="mb-4">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Projected Growth Metrics</label>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-12 bg-slate-100 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      <div className="col-span-3 px-3 py-2">Metric</div>
                      <div className="col-span-2 px-3 py-2 text-center">Current</div>
                      <div className="col-span-2 px-3 py-2 text-center">Projected</div>
                      <div className="col-span-1 px-3 py-2 text-center">Growth</div>
                      <div className="col-span-4 px-3 py-2">Justification</div>
                    </div>
                    {outcomes.metrics.map((m, i) => (
                      <div key={i} className={`grid grid-cols-12 text-xs ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b border-slate-100 last:border-0`}>
                        <div className="col-span-3 px-3 py-2.5 font-medium text-slate-700">{m.metric}</div>
                        <div className="col-span-2 px-3 py-2.5 text-center text-slate-500">{m.current}</div>
                        <div className="col-span-2 px-3 py-2.5 text-center font-semibold text-green-700">{m.projected}</div>
                        <div className="col-span-1 px-3 py-2.5 text-center">
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">{m.growth || m.change || '-'}</span>
                        </div>
                        <div className="col-span-4 px-3 py-2.5 text-[11px] text-slate-400 leading-tight">{m.justification}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Wins & Risks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {outcomes.keyWins?.length > 0 && (
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                    <h4 className="text-[10px] font-bold text-green-700 uppercase tracking-wider mb-2">
                      <i className="fas fa-trophy mr-1"></i>Expected Key Wins
                    </h4>
                    <ul className="space-y-1.5">
                      {outcomes.keyWins.map((w, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] text-green-800">
                          <i className="fas fa-check-circle text-green-500 mt-0.5 text-[9px] shrink-0"></i>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {outcomes.risks?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <h4 className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">
                      <i className="fas fa-exclamation-triangle mr-1"></i>Potential Challenges
                    </h4>
                    <ul className="space-y-1.5">
                      {outcomes.risks.map((r, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] text-amber-800">
                          <i className="fas fa-info-circle text-amber-500 mt-0.5 text-[9px] shrink-0"></i>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Next Steps */}
              {outcomes.nextSteps?.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-2">
                    <i className="fas fa-arrow-right mr-1"></i>After 30 Days
                  </h4>
                  <ul className="space-y-1.5">
                    {outcomes.nextSteps.map((step, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                        <i className="fas fa-chevron-right text-slate-400 mt-0.5 text-[8px] shrink-0"></i>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* AI disclaimer */}
              <div className="mt-3 flex items-start gap-1.5 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <i className="fas fa-info-circle text-slate-300 text-[10px] mt-0.5 shrink-0"></i>
                <p className="text-[9px] text-slate-400 leading-relaxed">
                  These projections are AI-generated estimates based on industry benchmarks, your business profile, and the proposed content strategy.
                  They are intended as directional guidance only. Actual performance depends on content quality, consistency of posting, audience engagement,
                  market conditions, and numerous other factors. We recommend tracking actual metrics weekly to measure real progress.
                </p>
              </div>
            </div>
          )}

          {/* Download Button */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <button onClick={handleDownloadReport} disabled={isDownloading}
              className="w-full py-2.5 rounded-lg text-sm font-semibold bg-slate-700 text-white hover:bg-slate-800 shadow-sm flex items-center justify-center gap-2 transition-all">
              {isDownloading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-download"></i>}
              Download Full Report (HTML)
            </button>
            <p className="text-[10px] text-slate-400 text-center mt-2">Includes strategy summary, content calendar, projected outcomes, and brand identity</p>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setActiveTab(3)}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
              <i className="fas fa-arrow-left mr-1.5"></i>Back
            </button>
            <button onClick={() => setActiveTab(5)}
              className="px-5 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              Next: Approve & Launch <i className="fas fa-arrow-right ml-1.5"></i>
            </button>
          </div>
        </div>
        );
      })()}

      {/* ============ TAB 5: Approve & Launch ============ */}
      {activeTab === 5 && megaStrategy && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded bg-green-600 text-white flex items-center justify-center text-[10px] font-bold"><i className="fas fa-rocket text-[9px]"></i></div>
              <h2 className="text-sm font-bold text-slate-800">Approve & Launch</h2>
              <span className="text-[10px] text-slate-400 ml-auto">Final step before launching your content calendar</span>
            </div>

            {/* Checklist */}
            <div className="space-y-2 mb-5">
              {[
                { label: 'Strategy summary reviewed', done: true },
                { label: 'Channel strategy verified', done: true },
                { label: `${megaStrategy.calendar?.length || 30}-day content calendar reviewed`, done: true },
                { label: 'Strategy report downloaded', done: false, optional: true }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 py-1.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${item.done ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                    <i className={`fas ${item.done ? 'fa-check' : 'fa-minus'} text-[9px]`}></i>
                  </div>
                  <span className={`text-xs ${item.done ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                    {item.label}
                    {item.optional && <span className="text-[10px] text-slate-300 ml-1">(optional)</span>}
                  </span>
                </div>
              ))}
            </div>

            {/* Calendar renewal info */}
            {calendarExpiry && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-[11px] text-blue-700 font-medium">
                  <i className="fas fa-info-circle mr-1.5"></i>
                  This calendar covers 30 days. When it expires, you can regenerate a fresh calendar with updated content.
                  {getDaysRemaining() && <span className="font-bold ml-1">({getDaysRemaining()} days remaining)</span>}
                </p>
              </div>
            )}

            {!isApproved ? (
              <button onClick={handleApprove}
                className="w-full py-3 rounded-lg text-sm font-bold bg-green-600 text-white hover:bg-green-700 shadow-sm flex items-center justify-center gap-2 transition-all">
                <i className="fas fa-check-circle"></i> Approve & Launch Calendar
              </button>
            ) : (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <i className="fas fa-check-circle text-green-600 text-2xl mb-2"></i>
                  <p className="text-sm font-bold text-green-800">Strategy Approved</p>
                  <p className="text-[11px] text-green-600">Your 30-day content calendar is ready to launch</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { setIsApproved(false); setMegaStrategy(null); handleGenerate(); }}
                    className="px-4 py-2.5 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                    <i className="fas fa-redo mr-1.5"></i>Regenerate
                  </button>
                  <button onClick={handleLaunchCalendar}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm flex items-center justify-center gap-2 transition-all">
                    <i className="fas fa-rocket"></i> Launch Calendar
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-start">
            <button onClick={() => setActiveTab(4)}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
              <i className="fas fa-arrow-left mr-1.5"></i>Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
